import React, { useState, useRef, useEffect } from "react";

const CitationLinks = ({ sources, references, id, citationIndex }) => {
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);
  const iconRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  
  if (!sources || sources.length === 0) {
    return null;
  }

  const referenceIndices = [...new Set(
    sources
      .map(source => {
        const index = source.referenceIndex ?? 0;
        return (typeof index === 'number' && index >= 0 && index < references.length) ? index : null;
      })
      .filter(index => {
        if (index === null) return false;
        const reference = references[index];
        return !!reference?.uri;
      })
  )].sort((a, b) => a - b);

  if (referenceIndices.length === 0) {
    return null;
  }

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPopover(true);
    }, 150);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPopover(false);
    }, 200);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        iconRef.current &&
        !iconRef.current.contains(event.target)
      ) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPopover]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (showPopover && popoverRef.current && buttonRef.current) {
      const button = buttonRef.current;
      const popover = popoverRef.current;
      const buttonRect = button.getBoundingClientRect();
      
      const top = buttonRect.top - popover.offsetHeight - 8;
      let left = buttonRect.left;
      
      const popoverWidth = popover.offsetWidth;
      const viewportWidth = window.innerWidth;
      
      if (left + popoverWidth > viewportWidth - 16) {
        left = viewportWidth - popoverWidth - 16;
      }
      
      if (left < 16) {
        left = 16;
      }
      
      popover.style.top = `${top}px`;
      popover.style.left = `${left}px`;
    }
  }, [showPopover]);

  const cleanTitle = (title) => {
    if (!title) return '';
    return title
      .replace(/[\x00-\x1F\x7F]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const firstReference = references[referenceIndices[0]];
  const remainingCount = referenceIndices.length - 1;

  const GlobeIcon = () => (
    <svg 
      width="16" 
      height="16" 
      viewBox="0 0 24 24" 
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
  );

  const allReferences = referenceIndices.map(index => references[index]).filter(ref => ref && ref.uri);
  const shouldShowPopover = allReferences.length > 1;

  return (
    <span 
      className="ai-overview__citation-chips"
      onMouseEnter={shouldShowPopover ? handleMouseEnter : undefined}
      onMouseLeave={shouldShowPopover ? handleMouseLeave : undefined}
    >
      {firstReference && firstReference.uri && (
        <a
          ref={iconRef}
          href={firstReference.uri}
          title={cleanTitle(firstReference.title) || firstReference.uri}
          className="ai-overview__citation-chip ai-overview__citation-chip--icon"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={cleanTitle(firstReference.title) || firstReference.uri}
          onMouseEnter={shouldShowPopover ? handleMouseEnter : undefined}
          onMouseLeave={shouldShowPopover ? handleMouseLeave : undefined}
        >
          <GlobeIcon />
        </a>
      )}
      
      {remainingCount > 0 && (
        <>
          <button
            ref={buttonRef}
            className="ai-overview__citation-chip ai-overview__citation-chip--count"
            onClick={() => setShowPopover(!showPopover)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            aria-label={`${remainingCount} more sources`}
            aria-expanded={showPopover}
          >
            +{remainingCount}
          </button>
          
          {showPopover && shouldShowPopover && (
            <div 
              ref={popoverRef}
              className="ai-overview__citation-popover"
              role="menu"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="ai-overview__citation-popover-title">Sources</div>
              <div className="ai-overview__citation-popover-items">
                {allReferences.map((reference, index) => {
                  const refIndex = referenceIndices[index];
                  if (!reference?.uri) return null;
                  
                  return (
                    <a
                      key={`${id}-popover-${citationIndex}-${refIndex}`}
                      href={reference.uri}
                      className="ai-overview__citation-popover-item"
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      aria-label={cleanTitle(reference.title) || reference.uri}
                    >
                      <GlobeIcon />
                      <span className="ai-overview__citation-popover-item-text">
                        {cleanTitle(reference.title) || reference.uri}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </span>
  );
};

export default CitationLinks;

